import { useState, type FormEvent } from "react";
import { Badge, Button, Input, cn } from "@carry/ui";
import { redeemInviteCode } from "./redeem";

interface PendingScreenProps {
  userName: string;
  apiBaseUrl: string;
  /** redeem 成功時に呼ばれる。親で session を再取得することを推奨 */
  onRedeemed: () => void | Promise<void>;
}

const ERROR_MESSAGES: Record<string, string> = {
  UNAUTHENTICATED: "セッションが切れました。アプリを開き直してください。",
  INVALID_BODY: "入力内容に誤りがあります。",
  INVALID_FORMAT: "招待コードの形式が正しくありません (英数字 8 桁)。",
  NOT_FOUND: "招待コードが見つかりません。",
  ALREADY_USED: "この招待コードは既に使用されています。",
  EXPIRED: "招待コードの有効期限が切れています。管理者に再発行を依頼してください。",
  REVOKED: "招待コードは取り消されています。",
  NO_CURRENT_USER: "現在のユーザーが見つかりません。",
  PLACEHOLDER_MISSING: "招待対象のユーザーが見つかりません。",
  RATE_LIMITED: "試行回数の上限に達しました。1 分ほど時間を置いてください。",
  REDEEM_FAILED: "招待コードの検証に失敗しました。",
};

export function PendingScreen({ userName, apiBaseUrl, onRedeemed }: PendingScreenProps) {
  const [code, setCode] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const result = await redeemInviteCode({ apiBaseUrl, code });
      if (!result.ok) {
        setError(
          result.error === "RATE_LIMITED" && result.retryAfterSec
            ? `試行回数の上限に達しました。約 ${result.retryAfterSec} 秒後に再試行してください。`
            : (ERROR_MESSAGES[result.error] ?? ERROR_MESSAGES.REDEEM_FAILED!),
        );
        return;
      }
      await onRedeemed();
    } catch (e) {
      setError(e instanceof Error ? e.message : "通信エラーが発生しました。");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div
      role="main"
      className={cn(
        "min-h-screen flex items-center justify-center",
        "bg-[var(--bg-sunken)] px-[var(--pad-x)] py-[var(--pad-y)]",
        "font-[var(--f-sans)]",
      )}
    >
      <div
        className={cn(
          "max-w-[420px] w-full",
          "bg-[var(--bg-raised)] border border-[var(--rule-soft)] rounded-lg",
          "p-[var(--pad-x)]",
        )}
      >
        {/* 承認待ちバッジ */}
        <Badge
          className={cn(
            "mb-3 bg-[var(--st-draft-bg,#fff3c4)] text-[var(--st-draft-ink,#8a5a00)]",
            "border-transparent text-[length:var(--fs-xs)] font-semibold",
          )}
        >
          承認待ち
        </Badge>

        <h1 className="text-[length:var(--fs-xl)] font-semibold mb-2">{userName} さん、ようこそ</h1>
        <p className="text-[length:var(--fs-sm)] text-[var(--ink-2)] leading-relaxed mb-4">
          LINE でのログインは完了しました。
          <br />
          ご利用開始には管理者から受け取った
          <strong> 招待コード </strong>
          の入力が必要です。
        </p>

        <form onSubmit={onSubmit} className="flex flex-col gap-[var(--gap)]">
          <label className="flex flex-col gap-1.5">
            <span className="text-[length:var(--fs-xs)] text-[var(--ink)]">招待コード</span>
            <Input
              type="text"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              autoComplete="off"
              autoFocus
              maxLength={20}
              placeholder="例: ABCDEFGH"
              disabled={submitting}
              className={cn(
                "font-[var(--f-mono)] tracking-[.15em] text-base uppercase",
                "h-[var(--row-h)] border-[var(--rule-soft)]",
              )}
            />
          </label>

          {error && (
            <div
              role="alert"
              className={cn(
                "text-[length:var(--fs-xs)] rounded-md px-[10px] py-2",
                "bg-[color-mix(in_srgb,var(--danger)_12%,transparent)]",
                "text-[var(--danger)] border border-[color-mix(in_srgb,var(--danger)_30%,transparent)]",
              )}
            >
              {error}
            </div>
          )}

          <Button
            type="submit"
            disabled={submitting || code.trim().length === 0}
            className={cn(
              "w-full min-h-[var(--row-h)] text-[length:var(--fs-md)] font-semibold",
              "bg-[var(--ok)] hover:bg-[var(--ok)]/90 text-white",
              submitting && "cursor-wait",
            )}
          >
            {submitting ? "確認中…" : "招待コードを使う"}
          </Button>
        </form>

        <p className="text-[length:var(--fs-xs)] text-[var(--ink-3)] text-center mt-3.5">
          招待コードの有効期限が切れている場合は、管理者に再発行を依頼してください。
        </p>
      </div>
    </div>
  );
}
